import React, { CSSProperties, FC, useState } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender, Row } from '@tanstack/react-table'

import { OrderOfGoActions } from './OrderOfGoActions'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../../../components/ui/tooltip'

import { cn } from '../../../../../../../lib/utils'

import { IBreakRegistrationByDay } from '../../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'

import { MANAGE_OPERATIONS_CONSTS } from '../../../../data/operations.data.const'

interface DraggableClassRowProps {
  tableRow: Row<IBreakRegistrationByDay>
  row: IBreakRegistrationByDay
  isLastChild: boolean
  rowIndex: number
  registrationsToDisplay: IBreakRegistrationByDay[]
  onOrderOfGoScratched: (row: IBreakRegistrationByDay, isScratched: boolean) => Promise<void>
  onScratch: (row: IRegistrationByDayInterface) => Promise<void>
  onRemoveBreak: (row: IBreakRegistrationByDay) => Promise<void>
}

export const DraggableClassRow: FC<DraggableClassRowProps> = ({
  tableRow,
  row,
  isLastChild,
  rowIndex,
  registrationsToDisplay,
  onOrderOfGoScratched,
  onScratch,
  onRemoveBreak,
}) => {
  const { transform, transition, setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: row.id,
  })
  const [orderOfGoScratchedLoading, setOrderOfGoScratchedLoading] = useState(false)
  const [scratchedLoading, setScratchedLoading] = useState(false)

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? registrationsToDisplay.length : registrationsToDisplay.length - rowIndex,
    position: 'relative',
    borderBottom: !isLastChild ? '1px solid rgb(233, 236, 239)' : 'none',
    minHeight: '72px',
    height: '70px',
    overflow: 'auto',
  }

  return (
    <tr ref={setNodeRef} style={style}>
      {tableRow.getVisibleCells().map((cell) => {
        const columnId = cell.id.split('_')[1] || cell.id
        return (
          <td
            key={cell.id}
            style={{
              width: MANAGE_OPERATIONS_CONSTS.CLASS_COLUMNS_WIDTH[columnId],
              paddingTop: '8px',
              paddingBottom: '8px',
              verticalAlign: 'middle',
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
      <td
        style={{
          width: MANAGE_OPERATIONS_CONSTS.CLASS_COLUMNS_WIDTH['drag'],
          paddingTop: '8px',
          paddingBottom: '8px',
          verticalAlign: 'middle',
        }}
      >
        <div className="flex justify-end gap-3  pr-2">
          <OrderOfGoActions
            row={row}
            onClickOrderOfGo={async () => {
              setOrderOfGoScratchedLoading(true)
              await onOrderOfGoScratched(row, !row.orderOfGoScratched)
              setOrderOfGoScratchedLoading(false)
            }}
            orderOfGoScratchedLoading={orderOfGoScratchedLoading}
            onClickScratch={async () => {
              setScratchedLoading(true)
              await onScratch(row as IRegistrationByDayInterface)
              setScratchedLoading(false)
            }}
            onRemoveBreak={async () => {
              setScratchedLoading(true)
              await onRemoveBreak(row)
              setScratchedLoading(false)
            }}
            scratchedLoading={scratchedLoading}
          />
          <div
            className={cn(
              'drag-handle justify-center flex',
              row.orderOfGoScratched && 'opacity-50'
            )}
            {...attributes}
            {...listeners}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="p-0 border-none bg-transparent opacity-40 hover:opacity-100 transition-all">
                  <img src="/assets/cp_icons/grip-vertical.svg" alt="Drag Row Icon" />
                </button>
              </TooltipTrigger>
              <TooltipContent hidden={!row.orderOfGoScratched}>
                <p>“Dropped” cannot be moved</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </td>
    </tr>
  )
}
